<!-- src/views/OnBoarding/EyesightDeclarationFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader />
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">Eyesight Declaration</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Visual Standards Compliance • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Medical Protocol</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">

          <!-- Dynamic Introductory Text -->
          <section 
            v-if="isLoadingText || (mainTextContent && mainTextContent !== '<p><br></p>')" 
            class="mb-12 p-8 bg-slate-50 rounded-3xl border border-slate-200 shadow-inner"
          >
            <div v-if="isLoadingText" class="text-slate-400 animate-pulse flex items-center gap-2">
               <div class="w-4 h-4 bg-slate-200 rounded-full"></div>
               <span class="font-black uppercase text-xs">Loading Vision Standards...</span>
            </div>
            <div v-else class="ql-editor !p-0 text-slate-700" v-html="mainTextContent"></div>
          </section>

          <form @submit.prevent="submitForm" class="space-y-16">
            
            <!-- Section 1: Your Details -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Identity Verification</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">First Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.firstName || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Last Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.lastName || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Verified Email</label>
                  <p class="text-lg font-black text-slate-800 lowercase tracking-tight">{{ userProfile.email || 'Loading...' }}</p>
                </div>
              </div>
            </section>

            <!-- Section 2: Eyesight Questionnaire -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <EyeIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Vision Assessment</h2>
              </div>

              <div class="space-y-6 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <QuestionYesNo id="wearsGlasses" label="Do You Wear Glasses?" v-model="formData.wearsGlasses" />
                <QuestionYesNo id="needsGlassesForStandard" label="Do You Need To Wear Glasses To Reach The Required Standard?" v-model="formData.needsGlassesForStandard" />
                <QuestionYesNo id="wearsContactLenses" label="Do You Wear Contact Lenses?" v-model="formData.wearsContactLenses" />
                
                <div v-if="formData.wearsContactLenses === true" class="pt-6 border-t border-slate-200 animate-fadeIn">
                    <QuestionYesNo id="carriesSpareGlasses" label="Do You Carry A Spare Pair Of Glasses At All Times When On Site?" v-model="formData.carriesSpareGlasses" />
                </div>
              </div>
            </section>

            <!-- Section 3: Confirmation -->
            <section class="bg-slate-900 p-10 rounded-[40px] text-white shadow-xl relative overflow-hidden">
              <div class="absolute top-0 right-0 p-10 opacity-5">
                <ShieldCheckIcon class="h-32 w-32" />
              </div>
              <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                  <div class="max-w-xl">
                    <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-2">3. Declaration</h2>
                    <p class="text-slate-400 font-medium leading-relaxed">By ticking this box, you formally confirm the information provided is true and correct to the best of your knowledge.</p>
                  </div>
                  <div class="bg-white/5 p-6 rounded-[32px] border border-white/10">
                      <div class="flex items-center">
                          <input id="confirmation" type="checkbox" v-model="formData.confirmation" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent transition-all hover:border-[#20FCF1]">
                          <label for="confirmation" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight">Accept &<br>Confirm <span class="text-red-500">*</span></label>
                      </div>
                  </div>
              </div>
            </section>

            <!-- Section 4: Signature Preview -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Digital Signature</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div class="max-w-md mx-auto">
                    <label class="form-label text-slate-400 text-center mb-6 uppercase tracking-widest">Official Record Signature</label>
                    <div class="bg-white p-8 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner flex flex-col items-center">
                        <div v-if="userProfile.signatureUrl || userProfile.signatureDataUrl" class="animate-fadeIn">
                            <img :src="userProfile.signatureUrl || userProfile.signatureDataUrl" alt="Signature Preview" class="max-h-24 object-contain" />
                            <div class="mt-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
                              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Verified Identity</span>
                            </div>
                        </div>
                        <div v-else class="text-center space-y-4">
                            <div class="bg-red-50 p-4 rounded-xl text-red-600 text-xs font-black uppercase tracking-tight border border-red-100">
                                Signature Missing from Profile
                            </div>
                            <button @click="$router.push({ name: 'yourDetails' })" type="button" class="px-6 py-2 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest">
                                Complete Profile
                            </button>
                        </div>
                    </div>
                  </div>
              </div>
            </section>

            <!-- Submit Button Area -->
            <div class="pt-10">
              <button 
                type="submit" 
                :disabled="!isFormValid || isSubmitting" 
                class="btn-premium disabled:bg-slate-200 disabled:text-slate-400"
              >
                <span v-if="isSubmitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Syncing...
                </span>
                <span v-else>Submit Eyesight Declaration</span>
              </button>
               <p v-if="!isFormValid && showValidationMessage" class="mt-6 text-center text-red-600 font-bold uppercase text-xs tracking-widest animate-pulse">
                Please complete all required questions and declaration
               </p>
            </div>
          </form>

          <div v-if="successMessage" class="mt-10 p-8 bg-mint-50 border border-brand-accent rounded-[32px] text-center shadow-lg animate-bounce font-black text-brand-primary uppercase tracking-widest">{{ successMessage }}</div>
          <div v-if="errorMessage" class="mt-10 p-8 bg-red-50 border border-red-200 rounded-[32px] text-center font-bold text-red-700"><strong>Error:</strong> {{ errorMessage }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import FormHeader from '@/components/FormHeader.vue';
import QuestionYesNo from '@/components/forms/QuestionYesNo.vue';

// Premium Icons
import { 
  IdentificationIcon, 
  EyeIcon, 
  ShieldCheckIcon, 
  PencilSquareIcon, 
  CheckBadgeIcon 
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { user } = useAuth();
const functions = getFunctions(firebaseApp, 'europe-west2');

const userProfile = reactive({ firstName: '', lastName: '', email: '', signatureUrl: '', signatureDataUrl: '' });
const formData = reactive({
  wearsGlasses: null,
  needsGlassesForStandard: null,
  wearsContactLenses: null,
  carriesSpareGlasses: null,
  confirmation: false,
});

const mainTextContent = ref('');
const isLoadingText = ref(true);
const isSubmitting = ref(false);
const showValidationMessage = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

const isFormValid = computed(() => {
    if (isLoadingText.value) return false;
    const data = formData;
    const baseQuestionsAnswered = data.wearsGlasses !== null && data.needsGlassesForStandard !== null && data.wearsContactLenses !== null;
    if (!baseQuestionsAnswered) return false;
    if (data.wearsContactLenses === true && data.carriesSpareGlasses === null) return false;
    return data.confirmation && (userProfile.signatureUrl || userProfile.signatureDataUrl);
});

watch(() => formData.wearsContactLenses, (newValue) => {
    if (newValue === false) {
        formData.carriesSpareGlasses = null;
    }
});

const fetchPrerequisites = async () => {
  if (!user.value) {
    errorMessage.value = "Authentication not found. Please log in again.";
    isLoadingText.value = false;
    return;
  }

  try {
    isLoadingText.value = true;
    const idTokenResult = await user.value.getIdTokenResult();
    const clientId = idTokenResult.claims.clientId;
    if (!clientId) throw new Error("Client association not found.");

    const [profileSnap, clientSnap] = await Promise.all([
        getDoc(doc(db, 'userProfiles', user.value.uid)),
        getDoc(doc(db, 'clients', clientId))
    ]);

    if (profileSnap.exists()) {
        const profile = profileSnap.data();
        userProfile.firstName = profile.firstName || '';
        userProfile.lastName = profile.lastName || '';
        userProfile.email = profile.email || 'No Email';
        userProfile.signatureUrl = profile.signatureUrl || '';
        userProfile.signatureDataUrl = profile.signatureDataUrl || '';
    } else {
        throw new Error("User profile not found. Please complete 'Your Details' first.");
    }

    if (clientSnap.exists()) {
      mainTextContent.value = clientSnap.data().onboardingFormTexts?.eyesightDeclaration || '';
    }

  } catch (error) {
    errorMessage.value = `Error loading prerequisites: ${error.message}`;
  } finally {
    isLoadingText.value = false;
  }
};

onMounted(fetchPrerequisites);

const submitForm = async () => {
  showValidationMessage.value = true;
  if (!isFormValid.value) return;
  
  isSubmitting.value = true;
  successMessage.value = '';
  errorMessage.value = '';

  const submissionData = {
    ...formData,
    firstName: userProfile.firstName,
    lastName: userProfile.lastName,
    email: userProfile.email,
    signatureUrl: userProfile.signatureUrl,
    signatureDataUrl: userProfile.signatureDataUrl,
    introductoryText: mainTextContent.value,
    status: 'completed',
  };

  try {
    const submitFunction = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFunction({
      formIdentifier: 'eyesightDeclaration',
      targetCollection: 'onboardingFormSubmissions',
      submissionData: submissionData
    });

    if (result.data.success) {
        successMessage.value = `Declaration submitted successfully! Reference: ${result.data.referenceNumber}`;
        setTimeout(() => router.push({ name: 'onboardingForms' }), 2000);
    } else {
        throw new Error(result.data.error || 'The form submission failed.');
    }
  } catch (error) {
    errorMessage.value = `Failed to submit form: ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all flex justify-center items-center; 
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Quill Styling */
:deep(.ql-editor) {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #334155;
}
:deep(.ql-editor p) {
  margin-bottom: 1.25rem;
}
:deep(.ql-editor h2) {
  @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4;
}
</style>